import { type RefObject, useEffect, useRef } from 'react';

interface UseInfiniteScrollOptions {
  onLoadMore: () => void;
  hasMore: boolean;
  isLoading: boolean;
  threshold?: number;
  rootMargin?: string;
  enabled?: boolean;
}

export function useInfiniteScroll({
  onLoadMore,
  hasMore,
  isLoading,
  threshold = 0.1,
  rootMargin = '0px',
  enabled = true,
}: UseInfiniteScrollOptions): {
  observerRef: RefObject<HTMLDivElement | null>;
} {
  const observerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!enabled || !hasMore || isLoading) {
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        const [entry] = entries;
        if (entry?.isIntersecting && hasMore && !isLoading) {
          onLoadMore();
        }
      },
      {
        threshold,
        rootMargin,
      },
    );

    const currentTarget = observerRef.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [enabled, hasMore, isLoading, onLoadMore, threshold, rootMargin]);

  return {
    observerRef,
  };
}
